import type { ImportedModelResourceOwnershipRegistry } from './ImportedModelResourceOwnershipRegistry'

/**
 * 一组导入模型资源的独立所有权句柄。
 *
 * 句柄只增加和释放引用，不复制 Three.js 资源。每个句柄最多释放一次，因而可以安全进入
 * 失败回滚、WorkAssembly 快照淘汰和 Editor 销毁等可能重复触发的清理路径。
 */
export class ImportedModelResourceLease implements PuppyCAD.ImportedModelResourceLeaseHandle {
  /** 当前句柄是否已经归还所有权。 */
  private released = false

  /** 当前句柄持有的去重资源集合；释放后立即断开强引用。 */
  private retainedResources: PuppyCAD.ImportedModelResourceSet | null

  /**
   * 创建资源租约。
   *
   * @param registry 统一维护真实资源引用数的注册表。
   * @param resources 已复制并去重的不可变资源集合。
   */
  public constructor(
    private readonly registry: ImportedModelResourceOwnershipRegistry,
    resources: PuppyCAD.ImportedModelResourceSet,
  ) {
    this.retainedResources = resources
  }

  /** 当前仍由租约持有的资源集合。 */
  public get resources(): PuppyCAD.ImportedModelResourceSet {
    if (!this.retainedResources) {
      throw new Error('已释放的导入模型资源租约不再持有资源集合')
    }
    return this.retainedResources
  }

  /**
   * 为相同真实资源创建另一个独立所有权句柄。
   *
   * @returns 复用同一 Object3D、Geometry、Material 和 Texture 的新句柄。
   */
  public retain(): ImportedModelResourceLease {
    if (this.released) {
      throw new Error('已释放的导入模型资源租约不能再次 retain')
    }
    return this.registry.acquire(this.resources)
  }

  /** 归还当前句柄持有的一次资源所有权。 */
  public release(): void {
    if (this.released) {
      return
    }
    this.released = true
    const resources = this.retainedResources
    this.retainedResources = null
    if (resources) {
      this.registry.release(resources)
    }
  }
}
